// The parts of the privacy notice that are facts about the deployment.
//
// **The same split `rulesComponents.tsx` makes**: prose stays prose, and
// anything that could be *wrong* asks the thing that knows. There it is a
// recipe's cost asking `RULES`; here it is "do we send email" asking the
// server, because the answer is an environment variable and the day somebody
// sets it is the day a static sentence starts lying.
//
// That is not hypothetical — this page has already carried three claims about
// an email system that did not exist. Written this way it cannot: switching
// mail on switches the sentence.

import { useEffect, useState } from "react";

import { Link } from "./kit";

/** Null while asking. The prose either side reads sensibly without this paragraph. */
function useMail(): boolean | null {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    fetch("/api/session")
      .then((response) => response.json())
      .then((session: { mail?: boolean }) => live && setOn(Boolean(session.mail)))
      // No answer is not "yes". A notice that claims we email you because a
      // fetch failed is the failure this component exists to prevent.
      .catch(() => live && setOn(false));
    return () => {
      live = false;
    };
  }, []);
  return on;
}

/** What the address is used for, which depends on whether mail is configured. */
export function MailUse(): React.JSX.Element | null {
  const on = useMail();
  if (on === null) return null;
  return on ? (
    <p data-testid="privacy-mail-on">
      Your address is used for <strong>two</strong> things and no others: to let you sign in, and to
      tell you once when you have been approved. It is not used for marketing, it is not passed to
      anyone, and there is no newsletter to be subscribed to by accident.
    </p>
  ) : (
    <p data-testid="privacy-mail-off">
      Your address is used for <strong>one</strong> thing: to let you sign in. It is not used for
      marketing, it is not passed to anyone, and — see below — nothing is emailed to it at all, so
      there is no newsletter to be subscribed to by accident. Being approved is something you find
      out by coming back and signing in again, rather than something we tell you.
    </p>
  );
}

/** Who, if anyone, handles outgoing mail on our behalf. */
export function MailHost(): React.JSX.Element | null {
  const on = useMail();
  if (on === null) return null;
  return on ? (
    <p>
      The one message we send — telling you that you have been let in — goes through{" "}
      <Link href="https://resend.com">Resend</Link>
      , which handles the delivery and nothing else. Like Railway they are a processor: they do not
      use any of it for anything of their own.
    </p>
  ) : (
    <p>
      <strong>We do not send email at all.</strong> There is no mail provider, no newsletter and
      nothing that will arrive in your inbox from us — your address is stored so that you can sign
      in, and that is the whole of what it does today.
    </p>
  );
}
